import { Button, Spacing, Text, Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { memo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { FAMILY_TARIFF_CONTENT_MAP } from 'reactApp/ui/FamilyTariffModal/FamilyTariffModalProps';
import type { FamilyTariffModalProps } from 'reactApp/ui/FamilyTariffModal/FamilyTariffModalTypes';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';

import styles from './FamilyTariffModal.css';

export const FamilyTariffModal = memo<FamilyTariffModalProps>(
    ({ isMobile, onShow, onClose, onDismiss, onPrimaryClick, onSecondaryClick }) => {
        const isDarkTheme = useSelector(isCurrentDarkTheme);

        useEffect(() => {
            onShow?.();
        }, []);

        const { title, textDesktop, textMobile, primaryActionText, secondaryActionText, lightImageUrls, darkImageUrls } =
            FAMILY_TARIFF_CONTENT_MAP;

        const imageUrls = isDarkTheme ? darkImageUrls : lightImageUrls;

        if (isMobile) {
            return (
                <MobileDialog
                    id={'extend-quota-offer-modal-mobile'}
                    className={classNames(styles.root_mobile, styles.dialogContent)}
                    onClose={onClose}
                    mod="base"
                    topmost
                    closeOnDimmerClick
                    contextPadding="zero"
                    innerClassNames={{
                        close: !isDarkTheme ? styles.close : styles.close_light,
                    }}
                    data-qa-id="family-tariff-modal-mobile"
                >
                    {imageUrls.mobile && (
                        <div className={styles.imageWrapper}>
                            <img className={styles.image} src={imageUrls.mobile} alt="Cover image" data-qa-id="family-tariff-modal-image" />
                        </div>
                    )}
                    <div className={styles.content} data-qa-id="family-tariff-modal-content">
                        <Title level="1" data-qa-id="family-tariff-modal-title">
                            {title}
                        </Title>
                        <Spacing size={12} />
                        <Text className={styles.text} data-qa-id="family-tariff-modal-content">
                            {textMobile}
                        </Text>
                        {primaryActionText && (
                            <>
                                <Spacing size={24} />
                                <Button
                                    className={styles.button}
                                    mode="primary"
                                    appearance="accent-invariable"
                                    target="_blank"
                                    onClick={onPrimaryClick}
                                    stretched
                                    size="l"
                                    data-qa-id="family-tariff-modal-primary-button"
                                >
                                    <div className={styles.buttonText}>{primaryActionText}</div>
                                </Button>
                            </>
                        )}
                        {secondaryActionText && (
                            <>
                                <Spacing size={8} />
                                <Button
                                    className={styles.button}
                                    mode="tertiary"
                                    appearance="neutral"
                                    target="_blank"
                                    onClick={onSecondaryClick}
                                    stretched
                                    size="l"
                                    data-qa-id="family-tariff-modal-secondary-button"
                                >
                                    <div className={styles.buttonText}>{secondaryActionText}</div>
                                </Button>
                            </>
                        )}
                        <Spacing size={16} />
                    </div>
                </MobileDialog>
            );
        }

        return (
            <WhatsNewDialog
                qaId="family-tariff-modal-desktop"
                className={styles.root}
                primaryButtonText={primaryActionText}
                secondaryButtonText={secondaryActionText}
                closeIconClassName={styles.cross}
                title={title}
                imageUrl={imageUrls.desktop}
                content={
                    <>
                        <Spacing size={12} />
                        <Text className={styles.text}>{textDesktop}</Text>
                        <Spacing size={24} />
                    </>
                }
                dialogSize="average"
                buttonTheme="vk"
                onClick={onPrimaryClick}
                onSecondary={onSecondaryClick}
                onClose={onClose}
                onDismiss={onDismiss}
                closeAfterClick
            />
        );
    }
);

FamilyTariffModal.displayName = 'FamilyTariffModal';
